/**
 * Other air traffic (non-mission ADS-B) for the LIVE map — context only, never separation.
 *
 * Fed by the mission socket (`{type:'traffic'}` replaces the whole picture, see lib/ws.ts) plus an
 * initial `GET …/traffic` when a live mission opens so the map is not empty until the next poll.
 * Targets older than TRAFFIC_MAX_AGE_SEC (by their own position time) are dropped, and anything
 * that is already a mission aircraft (same ICAO address or registration) is left to the mission
 * layer.
 */
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import type { Aircraft, EpochMs, Id, MissionBundle, TrafficTarget, WorldState } from '@apos/shared';
import { api } from '../lib/api';
import { serverNow } from '../lib/clock';
import { usePrefs } from '../lib/prefs';
import { onTrafficMessage } from '../lib/ws';
import { trafficName } from '../components/map/trafficStyle';

/** Drop a target this long after its last position (s). */
export const TRAFFIC_MAX_AGE_SEC = 60;

export interface TrafficState {
  missionId: Id | null;
  targets: TrafficTarget[];
  notes: string | null;
  /** Server time the picture last changed. */
  updatedAt: EpochMs | null;
  setPicture(missionId: Id, targets: readonly TrafficTarget[]): void;
  clear(): void;
}

export const useTraffic = create<TrafficState>((set, get) => ({
  missionId: null,
  targets: [],
  notes:null,
  updatedAt: null,
  setPicture(missionId, targets) {
    if (get().missionId !== missionId) return;
    set({ targets: [...targets], updatedAt: serverNow() });
  },
  clear() {
    set({ missionId: null, targets: [], updatedAt: null,notes:null });
  },
}));

const norm = (s: string | null | undefined) => (s ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Targets worth drawing at `now`: fresh (≤ maxAgeSec old, future-dated tolerated), valid
 * coordinates, not on the ground, and not already one of the mission's aircraft.
 */
export function visibleTraffic(
  targets: readonly TrafficTarget[],
  now: EpochMs,
  missionAircraft: readonly Pick<Aircraft, 'icao24' | 'registration'>[] = [],
  maxAgeSec = TRAFFIC_MAX_AGE_SEC,
): TrafficTarget[] {
  const hexes = new Set<string>();
  const regs = new Set<string>();
  for (const a of missionAircraft) {
    if (a.icao24) hexes.add(a.icao24.toLowerCase());
    const r = norm(a.registration);
    if (r) regs.add(r);
  }
  const out: TrafficTarget[] = [];
  for (const t of targets) {
    if (!Number.isFinite(t.lat) || !Number.isFinite(t.lon)) continue;
    const targetMaxAgeSec = t.source === 'flightaware' ? Math.max(maxAgeSec, 150) : maxAgeSec;
    if (now - t.t > targetMaxAgeSec * 1000) continue;
    if (t.onGround) continue;
    if (hexes.has(t.hex.toLowerCase())) continue;
    const r = norm(t.registration);
    if (r && regs.has(r)) continue;
    out.push(t);
  }
  return out;
}

export { trafficName };

/**
 * Keep the traffic picture fed while `active` (a live mission is open and the layer is wanted):
 * one GET for the current picture, then WS pictures. Clears when the mission changes.
 */
export function useLiveTraffic(missionId: Id | null, active: boolean): void {
  useEffect(() => {
    const st = useTraffic.getState();
    if (st.missionId !== missionId) useTraffic.setState({ missionId, targets: [], updatedAt: null,notes:null });
    if (!missionId || !active) return;
    let cancelled = false;
    const off = onTrafficMessage((id, targets) => {
      if (id === missionId) useTraffic.getState().setPicture(missionId, targets);
    });
    const refresh=()=>{const before=useTraffic.getState().updatedAt;return api
      .traffic(missionId)
      .then((r) => {
        // a WS picture that raced ahead is newer: only seed an empty store
        if (!cancelled && useTraffic.getState().updatedAt===before) {useTraffic.getState().setPicture(missionId, r.targets);useTraffic.setState({notes:(r.sources??[]).map(s=>s.notes).join(' ')||'Traffic coverage unknown.'});}
      })
      .catch(() => {
        if(!cancelled)useTraffic.setState({notes:'Traffic picture unavailable. Check your connection and provider settings.'});
      });};
    void refresh();
    const timer=setInterval(()=>{if(document.visibilityState==='visible')void refresh();},120_000);
    return () => {
      clearInterval(timer);
      cancelled = true;
      off();
    };
  }, [missionId, active]);
}

/**
 * Traffic for the LIVE map: fed while the mission is live and "Other traffic" is on; filtered to
 * fresh, airborne, non-mission targets at the world time. `undefined` when the mission is not
 * live (the map then offers no traffic switch).
 */
export function useMapTraffic(bundle: MissionBundle | null, world: WorldState | null): TrafficTarget[] | undefined {
  const show = usePrefs((s) => s.showTraffic);
  const live = bundle?.mission.status === 'live';
  useLiveTraffic(bundle?.mission.id ?? null, live && show);
  const raw = useTraffic((s) => s.targets);
  const aircraft = bundle?.aircraft;
  const t = world?.t ?? null;
  return useMemo(() => (live ? visibleTraffic(raw, t ?? serverNow(), aircraft) : undefined), [live, raw, t, aircraft]);
}

/** Payload that tracks a traffic target as a mission Watch aircraft (public ADS-B feed). */
export function watchAircraftFromTraffic(t: Pick<TrafficTarget, 'hex' | 'registration' | 'callsign' | 'typeCode'>) {
  const reg = t.registration?.trim() || null;
  const hex = t.hex.toLowerCase();
  return {
    label: trafficName(t).slice(0, 60),
    registration: reg ? reg.slice(0, 16) : null,
    icao24: /^[0-9a-f]{6}$/.test(hex) ? hex : null,
    // AeroAPI traffic targets have a FlightAware callsign even when registration is not published.
    flightAwareIdent: (reg || (t as Pick<TrafficTarget, 'callsign'>).callsign?.trim() || null)?.slice(0, 16) ?? null,
    typeDesignator: t.typeCode?.trim() || null,
    // Keep the production role honest while retaining the selected target's polling priority.
    role: 'other' as const,
    trackingPriority: 'high' as const,
    color: '#BFBFBF',
    provider: 'public' as const,
    wingspanM: null,
  };
}
